/**
 * Structure Studio shell — structure-first editor for gTwin scene items.
 * Route: `/studio/t/:gtwinId` (ADR-120).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { tokens } from '@/design';
import type { GTwinRecord } from '@/sdk-stub/gtwin-sdk/types';
import { gtwinClient } from '@/app/gtwinClientSingleton';
import { SyncStatusBadge } from '@/components/gtwin/SyncStatusBadge';
import { RealBackendBanner } from '@/components/gtwin/RealBackendBanner';
import { useSyncStatusStore } from '@/state/useSyncStatusStore';
import { StructureSlotTree } from './studio/StructureSlotTree';
import {
  StructureInspector,
  baselineAnchorDraft,
  baselineItemDraft,
} from './studio/StructureInspector';
import { LayerTransferPanel } from './studio/LayerTransferPanel';
import {
  anchorDraftFromRecord,
  buildItemPatch,
  buildProjectAnchorFromDraft,
  extractLayerOrder,
  extractStructureItems,
  itemDraftFromRecord,
  moveLayerInOrder,
  type AnchorInspectorDraft,
  type ItemInspectorDraft,
} from './studio/sceneStructure';
import { sceneItemToGlayerLayer } from './studio/sceneItemToGlayerLayer';
import { downloadBlob, exportSceneItemAsGlayer } from './studio/glayerExportClient';

export interface StructureStudioShellProps {
  gtwinId: string;
  initialRecord: GTwinRecord;
  onRecordChange: (record: GTwinRecord) => void;
}

export function StructureStudioShell({
  gtwinId,
  initialRecord,
  onRecordChange,
}: StructureStudioShellProps) {
  const [record, setRecord] = useState(initialRecord);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [itemDraft, setItemDraft] = useState<ItemInspectorDraft | null>(null);
  const [itemBaseline, setItemBaseline] = useState<ItemInspectorDraft | null>(null);
  const [anchorDraft, setAnchorDraft] = useState<AnchorInspectorDraft>(() =>
    anchorDraftFromRecord(initialRecord),
  );
  const [_anchorBaseline, setAnchorBaseline] = useState<AnchorInspectorDraft>(() =>
    anchorDraftFromRecord(initialRecord),
  );
  const [layerOrder, setLayerOrder] = useState<string[]>(() =>
    extractLayerOrder(initialRecord),
  );
  const [layerOrderBaseline, setLayerOrderBaseline] = useState<string[]>(() =>
    extractLayerOrder(initialRecord),
  );
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [transferItemIds, setTransferItemIds] = useState<Set<string>>(() => new Set());
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const markDirty = useSyncStatusStore((s) => s.markDirty);
  const markSaving = useSyncStatusStore((s) => s.markSaving);
  const markClean = useSyncStatusStore((s) => s.markClean);
  const markFailed = useSyncStatusStore((s) => s.markFailed);
  const resetSync = useSyncStatusStore((s) => s.resetSync);

  useEffect(() => {
    resetSync();
    markClean();
  }, [gtwinId, resetSync, markClean]);

  useEffect(() => {
    setRecord(initialRecord);
    setAnchorDraft(anchorDraftFromRecord(initialRecord));
    setAnchorBaseline(anchorDraftFromRecord(initialRecord));
    const order = extractLayerOrder(initialRecord);
    setLayerOrder(order);
    setLayerOrderBaseline(order);
  }, [initialRecord]);

  const entries = useMemo(() => extractStructureItems(record), [record]);
  const selected = useMemo(
    () => entries.find((e) => String(e.item.id) === selectedItemId) ?? null,
    [entries, selectedItemId],
  );

  const selectItem = useCallback(
    (itemId: string) => {
      setSelectedItemId(itemId);
      const entry = entries.find((e) => String(e.item.id) === itemId);
      if (entry) {
        const draft = itemDraftFromRecord(entry.item);
        setItemDraft(draft);
        setItemBaseline(draft);
      }
    },
    [entries],
  );

  const handleItemDraftChange = useCallback(
    (draft: ItemInspectorDraft) => {
      setItemDraft(draft);
      markDirty();
    },
    [markDirty],
  );

  const handleAnchorDraftChange = useCallback(
    (draft: AnchorInspectorDraft) => {
      setAnchorDraft(draft);
      markDirty();
    },
    [markDirty],
  );

  const handleMove = useCallback(
    (itemId: string, direction: 'up' | 'down') => {
      const next = moveLayerInOrder(layerOrder, itemId, direction);
      if (!next) return;
      setLayerOrder(next);
      markDirty();
    },
    [layerOrder, markDirty],
  );

  const handleToggleTransfer = useCallback((itemId: string, checked: boolean) => {
    setTransferItemIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  }, []);

  const handleExportGlayer = useCallback(async () => {
    if (!selected) return;
    setExportBusy(true);
    setExportError(null);
    try {
      const layer = sceneItemToGlayerLayer(selected);
      const stem =
        (typeof selected.item.title === 'string' && selected.item.title.trim()) ||
        String(selected.item.id);
      const safeName = stem.replace(/[^\w.-]+/g, '-').slice(0, 64) || 'layer';
      const { fileName, blob } = await exportSceneItemAsGlayer(layer, safeName);
      downloadBlob(blob, fileName);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err));
    } finally {
      setExportBusy(false);
    }
  }, [selected]);

  const handleTransferComplete = useCallback(async () => {
    const fresh = await gtwinClient.get(gtwinId);
    setRecord(fresh);
    onRecordChange(fresh);
    const order = extractLayerOrder(fresh);
    setLayerOrder(order);
    setLayerOrderBaseline(order);
    setTransferItemIds(new Set());
    if (selectedItemId && !extractStructureItems(fresh).some((e) => String(e.item.id) === selectedItemId)) {
      setSelectedItemId(null);
      setItemDraft(null);
      setItemBaseline(null);
    }
    markClean(new Date().toISOString());
  }, [gtwinId, markClean, onRecordChange, selectedItemId]);

  const orderChanged =
    layerOrder.length !== layerOrderBaseline.length ||
    layerOrder.some((id, i) => id !== layerOrderBaseline[i]);

  const hasItemChanges =
    selectedItemId &&
    itemDraft &&
    itemBaseline &&
    buildItemPatch(selectedItemId, itemDraft, itemBaseline) !== null;

  const anchorPatch =
    buildProjectAnchorFromDraft(record.envelope?.projectAnchor, anchorDraft);

  const canSave = Boolean(hasItemChanges || anchorPatch || orderChanged);

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    markSaving();
    try {
      if (hasItemChanges && selectedItemId && itemDraft && itemBaseline) {
        const patch = buildItemPatch(selectedItemId, itemDraft, itemBaseline);
        if (patch) {
          await gtwinClient.scene.items.patch(gtwinId, [patch]);
        }
      }
      if (anchorPatch) {
        await gtwinClient.projectAnchor.set(gtwinId, anchorPatch);
      }
      if (orderChanged) {
        await gtwinClient.scene.items.reorder(gtwinId, layerOrder);
      }
      const fresh = await gtwinClient.get(gtwinId);
      setRecord(fresh);
      onRecordChange(fresh);
      setAnchorDraft(anchorDraftFromRecord(fresh));
      setAnchorBaseline(anchorDraftFromRecord(fresh));
      const order = extractLayerOrder(fresh);
      setLayerOrder(order);
      setLayerOrderBaseline(order);
      if (selectedItemId) {
        const entry = extractStructureItems(fresh).find(
          (e) => String(e.item.id) === selectedItemId,
        );
        if (entry) {
          const draft = itemDraftFromRecord(entry.item);
          setItemDraft(draft);
          setItemBaseline(draft);
        }
      }
      markClean(new Date().toISOString());
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSaveError(msg);
      markFailed(msg);
    } finally {
      setSaving(false);
    }
  }, [
    anchorPatch,
    canSave,
    gtwinId,
    hasItemChanges,
    itemBaseline,
    itemDraft,
    layerOrder,
    markClean,
    markFailed,
    markSaving,
    onRecordChange,
    orderChanged,
    selectedItemId,
  ]);

  const label = record.label ?? gtwinId;

  return (
    <div
      data-testid="structure-studio-shell"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: tokens.color.surface[0],
        fontFamily: tokens.font.family.ui,
      }}
    >
      <RealBackendBanner mode={gtwinClient.sceneItemsMode} />
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `${tokens.space[2]}px ${tokens.space[4]}px`,
          borderBottom: tokens.border.thin,
          background: tokens.color.surface[1],
          gap: tokens.space[3],
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[3] }}>
          <strong style={{ fontSize: tokens.font.size.lg }}>Structure Studio</strong>
          <span style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>
            {label}
          </span>
          <code style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.muted }}>
            {gtwinId}
          </code>
          <SyncStatusBadge />
        </div>
        <nav style={{ display: 'flex', alignItems: 'center', gap: tokens.space[3] }}>
          <button
            type="button"
            data-testid="structure-save"
            disabled={!canSave || saving}
            onClick={() => void handleSave()}
            style={{
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              borderRadius: tokens.radius.sm,
              border: tokens.border.thin,
              background: canSave
                ? tokens.color.accent.primary
                : tokens.color.surface[2],
              color: canSave ? tokens.color.ink.inverse : tokens.color.ink.muted,
              cursor: canSave && !saving ? 'pointer' : 'not-allowed',
              fontSize: tokens.font.size.sm,
              fontWeight: tokens.font.weight.medium,
            }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <Link
            to={`/t/${gtwinId}`}
            style={{
              color: tokens.color.accent.primary,
              fontSize: tokens.font.size.sm,
              textDecoration: 'none',
            }}
          >
            Preview in 3D →
          </Link>
          <Link
            to="/projects"
            style={{
              color: tokens.color.accent.primary,
              fontSize: tokens.font.size.sm,
              textDecoration: 'none',
            }}
          >
            All projects
          </Link>
        </nav>
      </header>

      {saveError ? (
        <div
          role="alert"
          style={{
            padding: tokens.space[2],
            background: tokens.color.surface[2],
            color: tokens.color.semantic.danger,
            fontSize: tokens.font.size.sm,
          }}
        >
          Save failed: {saveError}
        </div>
      ) : null}

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <aside
          aria-label="Scene structure tree"
          style={{
            width: 320,
            borderRight: tokens.border.thin,
            display: 'flex',
            flexDirection: 'column',
            background: tokens.color.surface[0],
          }}
        >
          <StructureSlotTree
            entries={entries}
            layerOrder={layerOrder}
            selectedItemId={selectedItemId}
            transferItemIds={transferItemIds}
            onSelect={selectItem}
            onToggleTransfer={handleToggleTransfer}
            onMove={handleMove}
          />
          <LayerTransferPanel
            sourceGtwinId={gtwinId}
            selectedItemIds={[...transferItemIds]}
            onTransferComplete={() => void handleTransferComplete()}
          />
        </aside>

        <main
          aria-label="Structure inspector"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            borderRight: showPreview ? tokens.border.thin : undefined,
          }}
        >
          <StructureInspector
            record={record}
            selected={selected}
            itemDraft={itemDraft}
            anchorDraft={anchorDraft}
            onItemDraftChange={handleItemDraftChange}
            onAnchorDraftChange={handleAnchorDraftChange}
            onExportGlayer={() => void handleExportGlayer()}
            exportBusy={exportBusy}
            exportError={exportError}
          />
        </main>

        <aside
          aria-label="3D preview"
          style={{
            width: showPreview ? 420 : 48,
            display: 'flex',
            flexDirection: 'column',
            background: tokens.color.surface[1],
            transition: 'width 0.2s ease',
          }}
        >
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            aria-expanded={showPreview}
            style={{
              padding: tokens.space[2],
              border: 'none',
              borderBottom: tokens.border.thin,
              background: tokens.color.surface[2],
              cursor: 'pointer',
              fontSize: tokens.font.size.xs,
              color: tokens.color.ink.secondary,
            }}
          >
            {showPreview ? 'Hide preview' : '3D'}
          </button>
          {showPreview ? (
            <iframe
              title="3D viewer preview"
              src={`/t/${gtwinId}`}
              style={{ flex: 1, border: 'none', width: '100%' }}
            />
          ) : null}
        </aside>
      </div>
    </div>
  );
}

export { baselineItemDraft, baselineAnchorDraft };
